import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Alert, Button } from '@mui/material';
// hooks
import useAuth from '../hooks/useAuth';
import useLocales from '../hooks/useLocales';
import usePermissions from '../hooks/usePermissions';
// routes
import { PATH_DASHBOARD } from '../routes/paths';
// locales
import { tokens } from '../locales/tokens';
import { TIER_LABELS, resolvePlanTier } from '../utils/planGating';

const GRACE_DAYS = 3;

function daysLeft(pastDueSince: string | null | undefined): number | null {
  if (!pastDueSince) return null;
  const since = new Date(pastDueSince).getTime();
  if (Number.isNaN(since)) return null;
  const deadline = since + GRACE_DAYS * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((deadline - Date.now()) / (24 * 60 * 60 * 1000)));
}

/**
 * Global banner when organization subscription is past_due (grace window).
 */
export default function SubscriptionPastDueAlert() {
  const { user } = useAuth();
  const { t } = useLocales();
  const { can } = usePermissions();

  const sub = (user as any)?.subscription;
  const status = sub?.status;
  const pastDueSince = sub?.past_due_since as string | null | undefined;
  const planTier = resolvePlanTier((user as any)?.plan_tier || 'starter');
  const tierLabel =
    planTier === 'custom'
      ? t(tokens.pages.billing.tier.custom.name)
      : TIER_LABELS[planTier] || String(planTier);

  const remaining = useMemo(() => daysLeft(pastDueSince), [pastDueSince]);

  if (!can('billing.manage')) return null;
  if (status !== 'past_due') return null;
  // After grace, BillingLockGuard takes over — don't show the soft banner.
  if (remaining !== null && remaining <= 0) return null;

  const daysLabel =
    remaining === null
      ? t(tokens.pages.billing.globalPastDueDays, { count: GRACE_DAYS })
      : remaining <= 0
        ? t(tokens.pages.billing.globalPastDueToday)
        : remaining === 1
          ? t(tokens.pages.billing.globalPastDueOneDay)
          : t(tokens.pages.billing.globalPastDueDays, { count: remaining });

  return (
    <Alert
      severity="error"
      sx={{ mb: 2, mx: { xs: 2, lg: 0 } }}
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to={PATH_DASHBOARD.organization.subscription}
        >
          {t(tokens.pages.billing.pastDueUpdatePayment)}
        </Button>
      }
    >
      {t(tokens.pages.billing.globalPastDueAlert, { tier: tierLabel, days: daysLabel })}
    </Alert>
  );
}
